<template>
  <v-app>
    <!-- 🔹 Menú lateral -->
    <v-navigation-drawer
      v-model="drawer"
      :mini-variant.sync="mini"
      :app="!isMobile"
      :permanent="!isMobile"
      :temporary="isMobile"
      :fixed="isMobile"
      class="drawer"
    >
      
      <!-- 🔥 LOGO + NOMBRE CENTRADOS VERTICALMENTE -->
      <div class="drawer-header" data-tour="brand">
        <v-list-item class="flex-column pt-8 text-center">
          <v-avatar size="80" class="mb-1 logo-avatar">
            <v-img :src="require('@/assets/logo.png')" alt="Konven.io" contain />
          </v-avatar>

          <v-list-item-title class="text-subtitle-1 font-weight-bold logo-title mt-2">
            Konven.io - {{ companyName || $t('common.unknownCompany') }}
          </v-list-item-title>
        </v-list-item>
      </div>

      <v-divider />

      <div v-if="isSuperAdmin" class="px-3 pt-4" data-tour="company">
        <v-select
          v-model="selectedCompanyId"
          :items="companies"
          item-text="name"
          item-value="id"
          :label="$t('appShell.activeCompany')"
          dense
          outlined
          hide-details
          prepend-inner-icon="mdi-domain"
          @change="selectCompany"
        />
      </div>

      <!-- MENÚ -->
      <v-list dense nav class="menu-list" data-tour="main-nav">
        <v-list-item
          v-for="item in baseItems"
          :key="item.titleKey"
          link
          :to="item.to"
          :data-tour="tourSelector(item.to.name)"
          class="rounded-lg my-1 menu-item"
          active-class="menu-active"
          @click="closeMobileDrawer"
        >

          <v-list-item-icon>
            <v-badge
              v-if="item.showBadge && pendingCount > 0"
              :content="pendingCount"
              color="deep-purple accent-3"
              overlap
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-badge>

            <v-icon v-else>{{ item.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ $t(item.titleKey) }}</v-list-item-title>
          </v-list-item-content>

        </v-list-item>
      </v-list>

      <template v-if="globalNavItems.length">
        <v-divider class="mx-4 my-2" />

        <v-list dense nav class="menu-list pt-0">
          <v-list-item
            v-for="item in globalNavItems"
            :key="item.titleKey"
            link
            :to="item.to"
            :data-tour="tourSelector(item.to.name)"
            class="rounded-lg my-1 menu-item"
            active-class="menu-active"
            @click="closeMobileDrawer"
          >
            <v-list-item-icon>
              <v-badge
                v-if="item.showAccessRequestsBadge && accessRequestsPendingCount > 0"
                :content="accessRequestsPendingCount"
                color="deep-purple accent-3"
                overlap
              >
                <v-icon>{{ item.icon }}</v-icon>
              </v-badge>

              <v-icon v-else>{{ item.icon }}</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>{{ $t(item.titleKey) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <v-divider class="mx-4 my-2" />

      <v-list dense nav class="menu-list pt-0">
        <v-list-item
          v-for="item in legalItems"
          :key="item.titleKey"
          link
          :href="legalHref(item.to)"
          target="_blank"
          rel="noopener"
          class="rounded-lg my-1 menu-item"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ $t(item.titleKey) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-spacer></v-spacer>
      <v-divider />

      <!-- USUARIO -->
      <v-list-item class="user-info mt-2 mb-3 px-4" v-if="user">
        <v-avatar size="36" class="mr-3">
          <v-icon color="#3f3d56">mdi-account-circle</v-icon>
        </v-avatar>

        <v-list-item-content>
          <v-list-item-title class="font-weight-medium text-truncate">
            {{ user.displayName || user.nombre || $t('common.unknownUser') }}
          </v-list-item-title>

          <v-list-item-subtitle class="text-caption grey--text text--darken-1">
            {{ user.email }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="mb-3" />

      <!-- LOGOUT -->
      <v-list dense nav class="logout-section" data-tour="user-actions">
        <v-list-item
          v-if="!isMobile"
          link
          @click="startTour"
          class="tour-help-item rounded-lg mb-2"
          data-tour="tour-help"
        >
          <v-list-item-icon>
            <v-icon color="primary">mdi-help-circle-outline</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="primary--text font-weight-medium">
              {{ $t('tutorial.open') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="canChangePassword"
          link
          @click="openPasswordDialog"
          class="password-item rounded-lg mb-2"
        >
          <v-list-item-icon>
            <v-icon color="primary">mdi-lock-reset</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="primary--text font-weight-medium">
              {{ $t('appShell.changePassword') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link @click="logoutUser" class="logout-item rounded-lg mb-2">
          <v-list-item-icon>
            <v-icon color="red darken-1">mdi-logout</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="red--text text--darken-1 font-weight-medium">
              {{ $t('appShell.logout') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link @click="confirmDeleteAccount" class="delete-account-item rounded-lg mb-2">
          <v-list-item-icon>
            <v-icon color="red darken-2">mdi-delete-alert</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="red--text text--darken-2 font-weight-medium">
              {{ $t('appShell.deleteAccount') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div class="app-version">
        v{{ appVersion }}
      </div>

    </v-navigation-drawer>

    <!-- CONTENIDO -->
    <v-app-bar v-if="isMobile" app color="#eae7f8" flat class="mobile-app-bar">
      <v-btn icon @click="drawer = true">
        <v-icon>mdi-menu</v-icon>
      </v-btn>

      <v-avatar size="34" class="mx-2 logo-avatar">
        <v-img :src="require('@/assets/logo.png')" alt="Konven.io" contain />
      </v-avatar>

      <v-toolbar-title class="mobile-title">
        Konven.io - {{ companyName || $t('common.unknownCompany') }}
      </v-toolbar-title>
    </v-app-bar>

    <v-main>
      <v-container fluid class="main-container pa-2">
        <router-view :key="companyId || 'no-company'" />
      </v-container>
    </v-main>

    <v-dialog v-model="deleteDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          {{ $t('appShell.deleteTitle') }}
        </v-card-title>
        <v-card-text>
          {{ $t('appShell.deleteText') }}
          <v-alert v-if="deleteError" type="error" dense class="mt-4">
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false" :disabled="deletingAccount">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="red darken-2" dark @click="deleteCurrentAccount" :loading="deletingAccount">
            {{ $t('appShell.deleteConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="passwordDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          {{ $t('appShell.changePassword') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="passwordForm" v-model="passwordValid">
            <v-text-field
              v-model="passwordForm.currentPassword"
              :label="$t('appShell.currentPassword')"
              outlined
              dense
              type="password"
              prepend-inner-icon="mdi-lock-outline"
              :rules="[rules.required]"
            />

            <v-text-field
              v-model="passwordForm.newPassword"
              :label="$t('appShell.newPassword')"
              outlined
              dense
              type="password"
              prepend-inner-icon="mdi-lock-plus-outline"
              :rules="[rules.required, rules.password]"
            />

            <v-text-field
              v-model="passwordForm.confirmPassword"
              :label="$t('appShell.confirmPassword')"
              outlined
              dense
              type="password"
              prepend-inner-icon="mdi-lock-check-outline"
              :rules="[rules.required, rules.passwordMatch]"
            />
          </v-form>

          <v-alert v-if="passwordMessage" :type="passwordMessageType" dense class="mt-2">
            {{ passwordMessage }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closePasswordDialog" :disabled="changingPassword">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="primary" @click="updateCurrentPassword" :loading="changingPassword">
            {{ $t('appShell.changePassword') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <app-tour v-if="!isMobile" ref="appTour" />

  </v-app>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { db } from "@/firebase";
import { collection, getDocs, query, where, onSnapshot, doc } from "firebase/firestore";
import { getUserErrorMessage } from "@/utils/errors";
import packageInfo from "../../package.json";
import AppTour from "@/components/AppTour.vue";

export default {
  components: { AppTour },

  data() {
    return {
      drawer: true,
      mini: false,
      pendingCount: 0,
      accessRequestsPendingCount: 0,
      deleteDialog: false,
      deleteError: "",
      deletingAccount: false,
      passwordDialog: false,
      passwordValid: false,
      changingPassword: false,
      passwordMessage: "",
      passwordMessageType: "success",
      passwordForm: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      companies: [],
      selectedCompanyId: null,
      unsubscribeCompany: null,
      unsubscribePending: null,
      unsubscribeAccessRequestsPending: null,

      /* 🔥 Nombre de la empresa dinámico */
      companyName: null,

      baseItems: [
        { titleKey: "appShell.nav.notices", icon: "mdi-bulletin-board", to: { name: "notices" } },
        { titleKey: "appShell.nav.agreement", icon: "mdi-book-open-page-variant", to: { name: "agreement" } },
        { titleKey: "appShell.nav.calendar", icon: "mdi-calendar", to: { name: "calendar" } },
        {
          titleKey: "appShell.nav.workers",
          icon: "mdi-account-group-outline",
          to: { name: "workers" },
          showBadge: true,
        },
      ],
      globalAdminItems: [
        { titleKey: "appShell.nav.suggestions", icon: "mdi-chat-question", to: { name: "suggestions" } },
        {
          titleKey: "appShell.nav.accessRequests",
          icon: "mdi-account-clock",
          to: { name: "access-requests" },
          showAccessRequestsBadge: true,
        },
        { titleKey: "appShell.nav.managers", icon: "mdi-account-tie", to: { name: "managers" } },
      ],
      managerGlobalItems: [
        { titleKey: "appShell.nav.suggestions", icon: "mdi-chat-question", to: { name: "suggestions" } },
      ],
      legalItems: [
        { titleKey: "info.legalTitle", icon: "mdi-file-document-outline", to: { name: "legal-advice" } },
        { titleKey: "info.privacyTitle", icon: "mdi-shield-lock-outline", to: { name: "privacy-policy" } },
        { titleKey: "info.cookiesTitle", icon: "mdi-cookie-outline", to: { name: "cookies" } },
      ],
      appVersion: packageInfo.version,
    };
  },

  computed: {
    ...mapGetters("auth", ["user", "companyId", "isSuperAdmin", "canChangePassword"]),
    globalNavItems() {
      return this.isSuperAdmin ? this.globalAdminItems : this.managerGlobalItems;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    rules() {
      return {
        required: (value) => !!value || this.$t("common.required"),
        password: (value) => !!value && value.length >= 6 || this.$t("common.minPassword"),
        passwordMatch: (value) => value === this.passwordForm.newPassword || this.$t("login.passwordsMismatch"),
      };
    },
  },

  watch: {
    "$vuetify.breakpoint.smAndDown": {
      immediate: true,
      handler(isMobile) {
        this.drawer = !isMobile;
      },
    },
    $route() {
      this.closeMobileDrawer();
    },
    companyId(newVal) {
      if (newVal) {
        this.subscribeToPending();
        this.loadCompanyName();
      }
    },
  },

  mounted() {
    if (this.isSuperAdmin) {
      this.loadCompanies();
      this.selectedCompanyId = this.companyId;
      this.subscribeToAccessRequestsPending();
    }

    if (this.companyId) {
      this.subscribeToPending();
      this.loadCompanyName();
    }
  },

  beforeDestroy() {
    if (this.unsubscribeCompany) this.unsubscribeCompany();
    if (this.unsubscribePending) this.unsubscribePending();
    if (this.unsubscribeAccessRequestsPending) this.unsubscribeAccessRequestsPending();
  },

  methods: {
    ...mapActions("auth", ["changePassword", "deleteAccount", "logout", "setSelectedCompany"]),

    async logoutUser() {
      await this.logout();
      this.$router.push({ name: "landing" });
    },

    startTour() {
      if (this.isMobile) return;
      this.$refs.appTour?.start();
    },

    tourSelector(routeName) {
      return routeName ? `nav-${routeName}` : null;
    },

    closeMobileDrawer() {
      if (this.isMobile) this.drawer = false;
    },

    legalHref(to) {
      return this.$router.resolve(to).href;
    },

    confirmDeleteAccount() {
      this.deleteError = "";
      this.deleteDialog = true;
    },

    async deleteCurrentAccount() {
      this.deletingAccount = true;
      this.deleteError = "";

      try {
        await this.deleteAccount();
        this.deleteDialog = false;
        this.$router.push({ name: "landing" });
      } catch (error) {
        this.deleteError = getUserErrorMessage(error, "appShell.deleteError", this.$i18n);
      } finally {
        this.deletingAccount = false;
      }
    },

    openPasswordDialog() {
      this.passwordMessage = "";
      this.passwordMessageType = "success";
      this.passwordForm = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.passwordDialog = true;
      this.$nextTick(() => this.$refs.passwordForm?.resetValidation());
    },

    closePasswordDialog() {
      this.passwordDialog = false;
      this.passwordMessage = "";
    },

    async updateCurrentPassword() {
      if (!this.$refs.passwordForm.validate()) return;

      this.changingPassword = true;
      this.passwordMessage = "";

      try {
        await this.changePassword({
          currentPassword: this.passwordForm.currentPassword,
          newPassword: this.passwordForm.newPassword,
        });
        this.passwordMessage = this.$t("appShell.passwordChanged");
        this.passwordMessageType = "success";
        this.passwordForm = {
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        };
        this.$nextTick(() => this.$refs.passwordForm?.resetValidation());
      } catch (error) {
        this.passwordMessage = getUserErrorMessage(error, "appShell.passwordChangeError", this.$i18n);
        this.passwordMessageType = "error";
      } finally {
        this.changingPassword = false;
      }
    },

    async loadCompanies() {
      const snap = await getDocs(collection(db, "companies"));
      this.companies = snap.docs
        .map((companyDoc) => ({ id: companyDoc.id, ...companyDoc.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      const selectedExists = this.companies.some((company) => company.id === this.selectedCompanyId);

      if ((!this.selectedCompanyId || !selectedExists) && this.companies.length) {
        this.selectedCompanyId = this.companies[0].id;
        await this.selectCompany(this.selectedCompanyId);
      }
    },

    async selectCompany(companyId) {
      const company = this.companies.find((item) => item.id === companyId);
      await this.setSelectedCompany(company || null);
      this.companyName = company?.name || null;
      this.pendingCount = 0;
      this.subscribeToPending();

      if (this.$route.name === "managers") return;
      if (this.$route.name) {
        this.$router.replace({ name: this.$route.name }).catch(() => {});
      }
    },

    /* 🔥 CARGAR NOMBRE DE LA EMPRESA DESDE FIRESTORE */
    loadCompanyName() {
      if (this.unsubscribeCompany) this.unsubscribeCompany();
      if (!this.companyId) return;

      const ref = doc(db, "companies", this.companyId);

      this.unsubscribeCompany = onSnapshot(ref, (snap) => {
        if (snap.exists()) {
          this.companyName = snap.data().name || this.$t("common.unknownCompany");
        }
      });
    },

    /* 🔥 Escucha en tiempo real los trabajadores pendientes */
    subscribeToPending() {
      if (this.unsubscribePending) this.unsubscribePending();
      if (!this.companyId) return;

      const qPending = query(
        collection(db, "companyJoinRequests"),
        where("companyId", "==", this.companyId),
        where("status", "==", "pending")
      );

      this.unsubscribePending = onSnapshot(qPending, (snapshot) => {
        this.pendingCount = snapshot.docs.length;
      });
    },

    subscribeToAccessRequestsPending() {
      if (this.unsubscribeAccessRequestsPending) this.unsubscribeAccessRequestsPending();
      if (!this.isSuperAdmin) return;

      const qPending = query(
        collection(db, "requests"),
        where("status", "==", "pending")
      );

      this.unsubscribeAccessRequestsPending = onSnapshot(qPending, (snapshot) => {
        this.accessRequestsPendingCount = snapshot.docs.length;
      });
    },
  },
};
</script>

<style scoped>
/* 🔹 Drawer general */
.drawer {
  background-color: #eae7f8;
  border-right: none;
  min-width: 230px;

  /* 🔥 Necesario para centrar el header */
  display: flex;
  flex-direction: column;
}

/* 🔥 Centrar logo + nombre verticalmente */
.drawer-header {
  flex: 1; /* Ocupa todo el espacio posible arriba */
  display: flex;
  justify-content: center; /* Centrado vertical */
  align-items: center;     /* Centrado horizontal */
  flex-direction: column;
  text-align: center;
}

.logo-avatar {
  margin-top: 8px;
  border: 2px solid #d2c5f0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.logo-title {
  color: #3f3d56;
}

/* 🔹 Menú items */
.menu-list {
  padding-top: 10px;
}

.menu-item {
  color: #3f3d56;
  transition: all 0.2s ease-in-out;
}

.menu-item:hover {
  background-color: #d9d2f3;
}

.menu-active {
  background-color: #4a3aff !important;
  color: white !important;
}

.menu-active .v-icon,
.menu-active .v-list-item-title {
  color: white !important;
}

/* 🔹 Logout */
.logout-section {
  margin-top: auto;
  padding-bottom: 8px;
}

.logout-item {
  transition: background-color 0.2s ease;
  color: #3f3d56;
}

.logout-item:hover {
  background-color: #fbe9e7;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.logout-item .v-icon {
  transition: transform 0.2s ease;
}

.logout-item:hover .v-icon {
  transform: translateX(-3px);
}

.delete-account-item {
  color: #3f3d56;
}

.delete-account-item:hover {
  background-color: #ffebee;
}

.password-item:hover {
  background-color: #ede7f6;
}

/* 🔹 Usuario */
.user-info {
  background-color: #f4f1fc;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.user-info:hover {
  background-color: #e1dbf7;
}

.app-version {
  color: rgba(63, 61, 86, 0.52);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  padding: 0 16px 12px;
  text-align: center;
}

@media (max-width: 960px) {
  .drawer {
    max-width: 84vw;
  }

  .main-container {
    padding: 8px 6px 76px !important;
  }

  .mobile-app-bar {
    border-bottom: 1px solid rgba(63, 61, 86, 0.12) !important;
  }

  .mobile-title {
    color: #3f3d56;
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

</style>
